/**
 * Recording additions of the November 2026 redesign (docs/redesign-2026-11/README.md, Backend contract › Realdata;
 * contracts/ego.md §5), hand-written until realdata ships them:
 *
 * - Every label needs proof (`label_needs_evidence`): an object a frame (`dur_ms = 0`) or a clip; an activity, step or
 *   action a clip of 1–10 s.
 * - The place is an episode field, so its proof is `environment_evidence: [bookmark ids]` (at least one frame).
 * - Submitting (and sending a fix back) answers 422 `needs_labels` with the episode ids unless every episode has a
 *   place and a main activity, each with proof. Older app builds' captures are held as `needs_labels`.
 */
import { provesUnder, type AnnotationRules } from './app-config.ts'
import type { AnnotationDoc, Episode, EvidenceBookmark, LabelRole, MyCapture, MyCapturesPage } from './ego.ts'

/** Proof rules, as realdata checks them. */
export const PROOF_LIMITS = { minClipMs: 1000, maxClipMs: 10_000 } as const

/** An episode with the place's proof. */
export interface EpisodeWithProof extends Episode {
  environment_evidence?: string[]
}

export interface AnnotationDocWithProof extends Omit<AnnotationDoc, 'episodes'> {
  episodes: EpisodeWithProof[]
}

/** Problem codes for the label rules (PUT annotation 422; submit / resubmit 422). */
export type LabelRuleCode = 'label_needs_evidence' | 'environment_needs_evidence' | 'needs_labels'

/** `needs_labels` problem extras: the episodes still missing a place or a main activity with proof. */
export interface NeedsLabelsProblem {
  code: 'needs_labels'
  episodes: string[]
}

/** What kind of proof a label takes: a frame for a place or an object (an object may use a clip), a clip otherwise. */
export function proofKind(role: LabelRole | 'environment'): 'frame' | 'clip' {
  return role === 'environment' || role === 'object' ? 'frame' : 'clip'
}

/** Whether one bookmark can prove a label in this role. */
export function provesRole(role: LabelRole | 'environment', b: Pick<EvidenceBookmark, 'dur_ms'>, rules?: AnnotationRules): boolean {
  if (rules) return provesUnder(rules, role, b.dur_ms)
  if (role === 'environment') return b.dur_ms === 0
  if (role === 'object') return b.dur_ms === 0 || (b.dur_ms >= PROOF_LIMITS.minClipMs && b.dur_ms <= PROOF_LIMITS.maxClipMs)
  return b.dur_ms >= PROOF_LIMITS.minClipMs && b.dur_ms <= PROOF_LIMITS.maxClipMs
}

/** Capture summary fields the recordings table reads. */
export interface MyCaptureRow extends MyCapture {
  /** The server's words for a status this build doesn't know. */
  status_label?: string | null
}

/** A recording still on the phone: optional extras the phone reports (title, length, whether it is sending). */
export type PhoneRecording = NonNullable<MyCapturesPage['unsubmitted']>[number] & {
  title?: string | null
  duration_ms?: number | null
  /** on_phone: in the To finish list; sending: in the send queue. */
  phase?: 'on_phone' | 'sending' | null
}
